import {createSelector, Selector} from '@reduxjs/toolkit';

import {EEntityType} from 'shared/constants/entities';
import {TAppState} from 'store/config/types';

import {DATA_MODULE} from './data-constants';
import {
    IDataState,
    IEntityDataState,
} from './data-types';

const rootSelector: Selector<TAppState, IDataState> = (state: TAppState) => state[DATA_MODULE];

export const selectEntityData = (entityName: string, entityType: EEntityType) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[entityType],
);

export const selectIsEntityDataLoading = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => !!dataState[entityName]?.loading,
);

export const selectIsFormEntityDataEditable = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.FORM]?.isEditable,
);

export const selectFormEntityMode = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.FORM]?.formMode,
);

export const selectTableEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.TABLE],
);

export const selectTableStateEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.TABLE_STATE],
);

export const selectFormEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.FORM],
);

export const selectEditableTableEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.EDITABLE_TABLE],
);

export const selectFilterEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.FILTER],
);

export const selectReferenceEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.REFERENCE]?.rows,
);

export const selectReferencePlaceholder = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.REFERENCE]?.placeholder,
);

export const selectReferencesAsEntityDataArray = (referenceNames: string[]) => createSelector(
    rootSelector,
    (dataState: IDataState) => referenceNames.reduce<{[p: string]: IEntityDataState}>((acc, referenceName) => ({
        ...acc,
        [referenceName]: dataState[referenceName],
    }), {}),
);

export const selectReferenceDataLoading = (referenceName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => {
        const loading = dataState[referenceName]?.loading;
        if (loading === undefined) return false;
        return loading;
    },
);

export const selectTableSelectedRowKeys = (
    entityName: string,
) => createSelector(rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.TABLE]?.selectedRowKeys);

export const selectEntityDataFormInstance = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.FORM]?.formInstance,
);

export const selectDashboardEntityData = (entityName: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.[EEntityType.DASHBOARD],
);

export const selectUsedAdditionalOptionId = (entityName: string, entityNameChild: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName]?.FORM_FIELDS_DATA?.[entityNameChild]?.formUsedAdditionalOptionId,
);

export const selectUsedAdditionalOptions = (entityName: string, entityNameChild: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState[entityName].FORM_FIELDS_DATA?.options?.[entityNameChild],
);

export const selectFormDataWithEntityId = (entityName: string, id?: string) => createSelector(
    rootSelector,
    (dataState: IDataState) => {
        const data = dataState[entityName]?.[EEntityType.FORM];
        return data && data?.entityId === id ? data : undefined;
    },
);

export const selectNotificationDuration = () => createSelector(
    rootSelector,
    (dataState: IDataState) => dataState.notificationDuration,
);

export const selectSelectedSubsection = createSelector(
    rootSelector,
    (dataState: IDataState) => dataState.selectedSubsection,
);
